// Shared submission pipeline for intake forms.
// Used by the public fill page (IntakeFormFill) and the admin preview page
// (IntakeFormPreview). Pure DOM/data helpers — no React or component state.

import { isValidCurrency, isValidNumber } from './fieldInputs';

export const HIDDEN_PAYLOAD_FIELDS = new Set(['matter.name']);

export const ALLOWED_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'csv'];

const FORCE_ADDRESS_FIELDS = new Set([
    'contact.state',
    'contact.city',
    'contact.zip',
    'contact.address',
    'contact.address2',
]);

export const getFileExtension = (filename = '') => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const isAllowedFileType = (file) => {
    const ext = getFileExtension(file?.name || '');
    return ALLOWED_FILE_EXTENSIONS.includes(ext);
};

export const getRequiredFieldsFromSchema = (schema) => {
    if (!schema?.fields) return [];
    return Object.values(schema.fields)
        .filter((field) => field?.attrs?.required)
        .map((field) => ({
            id: field?.id,
            name: field?.attrs?.name || field?.config?.sys?.full_name,
            label: field?.config?.label || field?.attrs?.name || 'Required field',
            type: field?.attrs?.type || field?.tag,
        }))
        .filter((field) => field.name && !HIDDEN_PAYLOAD_FIELDS.has(field.name));
};

const isFieldEmpty = (type, elements) => {
    if (type === 'radio' || type === 'checkbox') return !elements.some((el) => el.checked);
    if (type === 'file') return !elements.some((el) => el.files && el.files.length > 0);
    return !elements[0].value?.trim();
};

// Each required field that is still empty, as { label, elements }.
const findMissingRequiredFields = (container, requiredFields) => {
    const missing = [];
    requiredFields.forEach(({ id, name, label, type }) => {
        // Formeo names checkbox and radio option inputs after the field id, not the schema name.
        const selectors = [`[name="${CSS.escape(name)}"]`];
        if (id) selectors.push(`[name="f-${CSS.escape(String(id))}"]`);
        const elements = Array.from(container.querySelectorAll(selectors.join(', ')));
        if (!elements.length) return;

        if (isFieldEmpty(type, elements)) missing.push({ label, elements });
    });

    return missing;
};

export const getMissingRequiredFields = (container, requiredFields) =>
    findMissingRequiredFields(container, requiredFields).map(({ label }) => label);

export const restructureFormData = (formData, formFields) => {
    const structuredData = {
        intake_form_id: formData.intake_form_id,
        contact: {},
        matter: {},
    };

    // Create an empty phone number array to store grouped phone number objects
    let phoneNumbers = [];

    // Loop through each field in the formData
    Object.keys(formData).forEach((key) => {
        const value = formData[key];

        // Find the field definition in the formFields object using the field name
        const field = Object.values(formFields.fields).find(field => field.config && field.config.sys && field.config.sys.full_name === key);

        if (field) {
            const category = field.config.category; // Get the category from the field config
            if (FORCE_ADDRESS_FIELDS.has(key)) {
                if (!structuredData.contact.address) {
                    structuredData.contact.address = {};
                }

                const addressKey = key.replace('contact.', '');
                structuredData.contact.address[addressKey] = value;
                return;
            }
            // Handle custom fields based on category (e.g., contact.custom_fields)
            if (category && category.startsWith('contact.custom_fields')) {
                if (!structuredData.contact.custom_fields) {
                    structuredData.contact.custom_fields = {}; // Initialize custom_fields if not already
                }
                const customField = key.replace('contact.', ''); // Remove 'contact.' prefix
                structuredData.contact.custom_fields[customField] = value;
            } else if (category && category.startsWith('matter.custom_fields')) {
                if (!structuredData.matter.custom_fields) {
                    structuredData.matter.custom_fields = {}; // Initialize custom_fields if not already
                }
                const customField = key.replace('matter.', ''); // Remove 'matter.' prefix
                structuredData.matter.custom_fields[customField] = value;
            } else if (category && category === 'contact.phones') {
                // Handle phone_number fields (contact.number, contact.title)
                const phoneField = key.split('.')[1]; // Get the field name after 'contact.phones.'

                // Find the corresponding phone number object in the array
                let phoneObj = phoneNumbers[0];

                if (!phoneObj) {
                    phoneObj = {};
                    phoneNumbers.push(phoneObj);
                }

                phoneObj[phoneField] = value;

                console.log('phoneObj', phoneObj);
                // Once both 'contact.title' and 'contact.number' are found, they will be grouped in the same object
            } else {
                // Regular fields, just map them to contact or matter
                const fieldCategory = key.split('.')[0]; // Either 'contact' or 'matter'
                const fieldName = key.split('.').slice(1).join('.'); // Get the field name after the prefix

                if (fieldCategory === 'contact') {
                    structuredData.contact[fieldName] = value;
                } else if (fieldCategory === 'matter') {
                    structuredData.matter[fieldName] = value;
                }
            }
        } else {
            // Handle cases where the field is not found (optional: log for debugging)
            console.warn(`Field not found for key: ${key}`);
        }
    });

    // If phoneNumbers array has data, assign it to contact.phone_number
    if (phoneNumbers.length > 0) {
        structuredData.contact.phone_number = phoneNumbers;
    }

    // Return the restructured data
    return structuredData;
};

export const humanizeFieldName = (name = '') => {
    if (!name) return 'Phone number';

    // remove first relation (e.g., "contact.home_number" -> "home_number")
    const withoutFirst = name.includes('.') ? name.split('.').slice(1).join('.') : name;

    // turn dots/underscores into spaces
    const spaced = withoutFirst.replace(/\./g, ' ').replace(/_/g, ' ');

    // Title Case
    return spaced.replace(/\b\w/g, (c) => c.toUpperCase()).trim();
};

export const getSingleCheckboxValue = (_, input) => Boolean(input?.checked);

// What you actually store/send
export const normalizePhoneForSubmit = (raw) => {
    const s = String(raw ?? '').trim();
    const hasPlus = s.startsWith('+');
    const digits = s.replace(/\D/g, '');
    return hasPlus ? (digits ? `+${digits}` : '') : digits;
};

export const isValidPhoneNormalized = (normalized) => {
    if (!normalized) return true; // "required" is handled elsewhere
    if (normalized.startsWith('+')) return /^\+\d{7,15}$/.test(normalized); // E.164-ish
    return /^\d{10}$/.test(normalized); // US 10-digit
};

export const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const applyCheckboxOtherToPayload = (rawData) => {
    Object.keys(rawData).forEach((key) => {
        if (!key.endsWith('__other')) return;

        const baseKey = key.replace('__other', '');
        const otherValue = rawData[key]?.trim();

        if (!otherValue) {
            delete rawData[key];
            return;
        }

        // MULTI checkbox
        if (Array.isArray(rawData[baseKey])) {
            rawData[baseKey] = [...rawData[baseKey], otherValue];
        }
        // SINGLE checkbox: preserve both the base checkbox state and the other text
        else {
            rawData[baseKey] = JSON.stringify({
                checked: Boolean(rawData[baseKey]),
                other: otherValue,
            });
        }

        delete rawData[key];
    });
};

// Collect + validate the rendered form, then build the submission FormData.
// Returns { missing, errors, problems, fd }:
//   - missing:  labels of unfilled required fields (caller shows one warning toast)
//   - errors:   file/email/phone validation messages (caller toasts each)
//   - problems: the same, per field, as { elements, message } for showFieldProblems
//   - fd:       ready-to-POST FormData (null when missing/errors present)
export const buildIntakeSubmission = ({
    container,
    schema,
    intakeFormId,
    requiredFields = [],
    fieldLabelMap = {},
}) => {
    const parsedSchema = typeof schema === 'string' ? JSON.parse(schema) : schema;

    const missingFields = findMissingRequiredFields(container, requiredFields);
    if (missingFields.length) {
        return {
            missing: missingFields.map(({ label }) => label),
            errors: [],
            problems: missingFields.map(({ elements }) => ({ elements, message: 'This field is required.' })),
            fd: null,
        };
    }

    const errors = [];
    const problems = [];
    const addError = (el, message) => {
        errors.push(message);
        problems.push({ elements: [el], message });
    };
    const rawData = {};
    const fd = new FormData();

    const elements = container.querySelectorAll('input, select, textarea');
    const checkboxGroupCounts = {};
    const fieldNameById = {};

    Object.values(parsedSchema?.fields || {}).forEach((field) => {
        if (!field?.id) return;
        const name = field?.attrs?.name || field?.config?.sys?.full_name;
        if (name) fieldNameById[`f-${field.id}`] = name;
    });

    elements.forEach((el) => {
        if (el.type !== 'checkbox' || !el.name) return;
        const baseName = el.name.endsWith('[]') ? el.name.slice(0, -2) : el.name;
        checkboxGroupCounts[baseName] = (checkboxGroupCounts[baseName] || 0) + 1;
    });

    elements.forEach((el) => {
        if (!el.name) return;
        if (HIDDEN_PAYLOAD_FIELDS.has(el.name)) return;

        if (el.type === 'file') {
            const files = Array.from(el.files || []);
            if (!files.length) return;

            files.forEach((file) => {
                if (!isAllowedFileType(file)) {
                    addError(el, `File "${file.name}" is not an allowed type.`);
                    return;
                }
                if (file.size > 100 * 1024 * 1024) {
                    addError(el, `File "${file.name}" exceeds the 100 MB size limit.`);
                } else {
                    const fileFieldName = el.name.endsWith('[]') ? el.name : `${el.name}[]`;
                    fd.append(fileFieldName, file);
                }
            });
            return;
        }

        if (el.type === 'email') {
            const value = el.value?.trim();
            if (value && !isValidEmail(value)) {
                addError(el, `Invalid email address: ${value}`);
            }
            rawData[el.name] = value;
            return;
        }

        const labelFor = () =>
            el.dataset.fieldLabel ||
            fieldLabelMap[el.name] ||
            humanizeFieldName(el.name);

        // data-input-kind is set by applyInputKinds (fieldInputs.js).
        if (el.dataset.inputKind === 'phone') {
            const normalized = normalizePhoneForSubmit(el.value);

            if (normalized && !isValidPhoneNormalized(normalized)) {
                addError(el, `Invalid ${labelFor()} "${el.value}". it must be a 10-digit US number.`);
            }

            rawData[el.name] = normalized; // store clean value
            return;
        }

        if (el.dataset.inputKind === 'number') {
            // A trailing point ("12.") is still being typed as far as the user is concerned.
            const value = el.value.trim().replace(/\.$/, '');

            if (value && !isValidNumber(value)) {
                addError(el, `${labelFor()} must be a number.`);
            }

            rawData[el.name] = value;
            return;
        }

        if (el.dataset.inputKind === 'currency') {
            const value = el.value.trim().replace(/\.$/, '');

            if (value && !isValidCurrency(value)) {
                addError(el, `${labelFor()} must be an amount, like 1250.00.`);
            }

            rawData[el.name] = value;
            return;
        }

        if (el.type === 'checkbox') {
            const baseName = el.name.endsWith('[]') ? el.name.slice(0, -2) : el.name;
            const resolvedName = fieldNameById[baseName] || baseName;
            const isGroup = checkboxGroupCounts[baseName] > 1;

            if (isGroup) {
                if (!rawData[resolvedName]) rawData[resolvedName] = [];
                if (el.checked) rawData[resolvedName].push(el.value);
                return;
            }

            rawData[resolvedName] = getSingleCheckboxValue(container, el);
            return;
        }

        if (el.type === 'radio') {
            if (el.checked) rawData[fieldNameById[el.name] || el.name] = el.value;
            return;
        }

        if (el.tagName === 'SELECT' && el.multiple) {
            rawData[el.name] = Array.from(el.selectedOptions).map((o) => o.value);
            return;
        }

        rawData[el.name] = el.value;
    });

    if (errors.length) {
        return { missing: [], errors, problems, fd: null };
    }

    rawData.intake_form_id = intakeFormId;
    applyCheckboxOtherToPayload(rawData);

    const structuredPayload = restructureFormData(rawData, parsedSchema);

    fd.append('data', JSON.stringify(structuredPayload));

    return { missing: [], errors: [], problems: [], fd };
};
